import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useScope } from "@/context/ScopeContext";
import DataTable from "../components/DataTable";
import { getBackendUrl } from "@/lib/backend";
import DataLoadError from "@/components/DataLoadError";
import HeroStatusChip from "@/components/HeroStatusChip";
import "./StateOverview.css";

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;
const PUNE_DISTRICT_CODE = "2725";

const fmt = (v) => (typeof v === "number" ? v.toLocaleString("en-IN") : v ?? "—");
const norm = (s) => String(s || "").trim().toUpperCase();

const getBlockBadge = (shi) => {
  if (shi < 90) return { label: "Attention", className: "sov-badge red" };
  if (shi < 92) return { label: "Monitor", className: "sov-badge warn" };
  return { label: "Excellent", className: "sov-badge" };
};

const getDistrictBadge = (shi) => {
  if (shi >= 90) return { label: "Excellent", className: "sov-badge" };
  if (shi >= 85) return { label: "Monitor", className: "sov-badge warn" };
  return { label: "Attention", className: "sov-badge red" };
};

const StateOverview = () => {
  const navigate = useNavigate();
  const { scope } = useScope();
  const topRef = useRef(null);
  const blocksRef = useRef(null);
  const insightsRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  const [kpiData, setKpiData] = useState(null);
  const [districts, setDistricts] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [view, setView] = useState("grid");
  const [sortBy, setSortBy] = useState("shi_score");
  const [sortOrder] = useState("desc");

  const districtCode = scope.districtCode || PUNE_DISTRICT_CODE;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setApiError(null);
    try {
      const [kpiRes, districtsRes, blocksRes] = await Promise.all([
        axios.get(`${API}/state/overview`),
        axios.get(`${API}/districts?sort_by=${sortBy}&sort_order=${sortOrder}`),
        axios.get(`${API}/districts/${districtCode}/blocks?sort_by=${sortBy}&sort_order=${sortOrder}`),
      ]);
      setKpiData(kpiRes.data);
      setDistricts(districtsRes.data);
      setBlocks(blocksRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      setApiError(error);
    } finally {
      setLoading(false);
    }
  }, [districtCode, sortBy, sortOrder]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const district = useMemo(() => {
    if (!districts.length) return null;
    return districts.find((d) => d.district_code === districtCode) || districts[0];
  }, [districts, districtCode]);

  const scopedBlocks = useMemo(() => {
    let list = [...blocks];
    if (scope.blockCode) {
      list = list.filter((b) => b.block_code === scope.blockCode);
    } else if (scope.blockName) {
      list = list.filter((b) => norm(b.block_name) === norm(scope.blockName));
    }
    return list;
  }, [blocks, scope.blockCode, scope.blockName]);

  const scopeKpi = useMemo(() => {
    const aggregateBlocks = (rows) => {
      if (!rows.length) return null;
      const totalSchools = rows.reduce((s, b) => s + (b.total_schools || 0), 0);
      const totalStudents = rows.reduce((s, b) => s + (b.total_students || 0), 0);
      const avg = (key) => {
        const vals = rows.map((b) => b[key]).filter((v) => typeof v === "number");
        return vals.length ? Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10 : 0;
      };
      return {
        total_schools: totalSchools,
        total_students: totalStudents,
        aadhaar_percentage: avg("aadhaar_percentage"),
        apaar_percentage: avg("apaar_percentage"),
        water_availability_percentage: avg("water_percentage"),
        avg_ptr: avg("avg_ptr"),
        data_entry_percentage: avg("data_entry_percentage"),
        avg_shi: avg("shi_score"),
      };
    };

    if (scopedBlocks.length > 0 && scopedBlocks.length < blocks.length) {
      return aggregateBlocks(scopedBlocks) || kpiData;
    }
    if (district) {
      return {
        total_schools: district.total_schools,
        total_students: district.total_students,
        aadhaar_percentage: district.aadhaar_percentage,
        apaar_percentage: district.apaar_percentage,
        water_availability_percentage: district.water_percentage,
        avg_ptr: district.avg_ptr,
        data_entry_percentage: district.data_entry_percentage,
        avg_shi: district.shi_score,
      };
    }
    return kpiData;
  }, [scopedBlocks, blocks.length, district, kpiData]);

  const scopeLabel =
    scope.schoolName?.trim() ||
    scope.blockName?.trim() ||
    scope.districtName?.trim() ||
    district?.district_name ||
    "Pune District";

  const metricSubtitles = useMemo(() => {
    const schools = scopeKpi?.total_schools ?? 0;
    const students = scopeKpi?.total_students ?? 0;
    const avgPerSchool =
      schools > 0 ? `${Math.round(students / schools).toLocaleString("en-IN")} students/school` : "—";
    const stateAadhaar = kpiData?.aadhaar_percentage;
    const scopeAadhaar = scopeKpi?.aadhaar_percentage;
    const aadhaarSub =
      typeof stateAadhaar === "number" && typeof scopeAadhaar === "number"
        ? scopeAadhaar >= stateAadhaar
          ? `${(scopeAadhaar - stateAadhaar).toFixed(1)} pts above state avg`
          : `${(stateAadhaar - scopeAadhaar).toFixed(1)} pts below state avg`
        : "District average";
    const stateApaar = kpiData?.apaar_percentage;
    const scopeApaar = scopeKpi?.apaar_percentage;
    const apaarSub =
      typeof scopeApaar === "number" && scopeApaar < 90
        ? "Needs acceleration"
        : typeof stateApaar === "number" && typeof scopeApaar === "number"
          ? scopeApaar >= stateApaar
            ? `${(scopeApaar - stateApaar).toFixed(1)} pts above state avg`
            : `${(stateApaar - scopeApaar).toFixed(1)} pts below state avg`
          : "District average";
    return {
      schools: scopeLabel,
      students: avgPerSchool,
      aadhaar: aadhaarSub,
      apaar: apaarSub,
      apaarWarn: typeof scopeApaar === "number" && scopeApaar < 90,
    };
  }, [scopeKpi, kpiData, scopeLabel]);

  const sortedBlocks = useMemo(() => {
    const list = [...scopedBlocks];
    const key = sortBy;
    list.sort((a, b) => {
      const av = a[key] ?? 0;
      const bv = b[key] ?? 0;
      if (typeof av === "string") return sortOrder === "desc" ? bv.localeCompare(av) : av.localeCompare(bv);
      return sortOrder === "desc" ? bv - av : av - bv;
    });
    return list;
  }, [scopedBlocks, sortBy, sortOrder]);

  const topBlocks = useMemo(
    () => [...scopedBlocks].sort((a, b) => b.shi_score - a.shi_score).slice(0, 5),
    [scopedBlocks],
  );

  const bottomBlocks = useMemo(
    () => [...scopedBlocks].sort((a, b) => a.shi_score - b.shi_score).slice(0, 5),
    [scopedBlocks],
  );

  const ragDistribution = useMemo(() => {
    const excellent = scopedBlocks.filter((b) => b.shi_score >= 85).length;
    const atRisk = scopedBlocks.filter((b) => b.shi_score >= 70 && b.shi_score < 85).length;
    const critical = scopedBlocks.filter((b) => b.shi_score < 70).length;
    return { excellent, atRisk, critical, total: scopedBlocks.length };
  }, [scopedBlocks]);

  const donutStyle = useMemo(() => {
    const total = Math.max(ragDistribution.total, 1);
    const excPct = (ragDistribution.excellent / total) * 100;
    const riskPct = (ragDistribution.atRisk / total) * 100;
    const critPct = (ragDistribution.critical / total) * 100;
    const excEnd = excPct;
    const riskEnd = excEnd + riskPct;
    return {
      background: `radial-gradient(circle at center, white 0 51%, transparent 52%), conic-gradient(var(--sov-success) 0 ${excEnd}%, var(--sov-warning) ${excEnd}% ${riskEnd}%, var(--sov-danger) ${riskEnd}% ${riskEnd + critPct}%, rgba(16,185,129,.14) ${riskEnd + critPct}% 100%)`,
    };
  }, [ragDistribution]);

  const gaugeStyle = useMemo(() => {
    const score = scopeKpi?.avg_shi ?? 0;
    const pct = Math.min(100, score);
    return {
      background: `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(var(--sov-success) 0 ${pct}%, rgba(16,185,129,.14) ${pct}% 100%)`,
    };
  }, [scopeKpi?.avg_shi]);

  const excellentBlocks = useMemo(() => scopedBlocks.filter((b) => b.shi_score >= 92).length, [scopedBlocks]);
  const attentionBlocks = useMemo(() => scopedBlocks.filter((b) => b.shi_score < 92).length, [scopedBlocks]);

  const identityIndex = useMemo(() => {
    const aadhaar = scopeKpi?.aadhaar_percentage ?? 0;
    const apaar = scopeKpi?.apaar_percentage ?? 0;
    return Math.round(((aadhaar + apaar) / 2) * 10) / 10;
  }, [scopeKpi]);

  const classroomHealth = useMemo(() => {
    if (!district) return 0;
    return Math.round(((district.water_percentage + district.toilet_percentage) / 2 + district.shi_score) / 2 * 10) / 10;
  }, [district]);

  const primaryActionText = useMemo(() => {
    const names = bottomBlocks.slice(0, 5).map((b) => b.block_name).join(", ");
    return names
      ? `Focus the next review on ${names} to protect district-level excellence.`
      : "Continue monitoring block-level indicators to protect district-level excellence.";
  }, [bottomBlocks]);

  const scrollTo = (ref) => ref?.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const blockTableColumns = [
    { key: "block_name", label: "Block", sortable: true },
    { key: "total_schools", label: "Schools", type: "number", sortable: true },
    { key: "total_students", label: "Students", type: "number", sortable: true },
    { key: "aadhaar_percentage", label: "Aadhaar %", type: "percentage", sortable: true },
    { key: "apaar_percentage", label: "APAAR %", type: "percentage", sortable: true },
    { key: "shi_score", label: "OSH Score", type: "progress", sortable: true },
    { key: "rag_status", label: "Status", type: "rag" },
    { key: "action", label: "", type: "action" },
  ];

  if (loading) {
    return (
      <div className="state-overview-view" data-testid="state-overview">
        <div className="sov-loading">
          <div className="sov-loading-spinner" />
          <span>Loading state overview…</span>
        </div>
      </div>
    );
  }

  if (apiError) {
    return (
      <div className="state-overview-view" data-testid="state-overview">
        <div className="sov-loading">
          <DataLoadError onRetry={fetchData} title="State overview could not be loaded" />
        </div>
      </div>
    );
  }

  const districtBadge = getDistrictBadge(district?.shi_score ?? scopeKpi?.avg_shi ?? 0);

  return (
    <div className="state-overview-view" data-testid="state-overview" ref={topRef}>
      <section className="sov-page-header">
        <div>
          <div className="sov-eyebrow">
            <svg className="sov-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 3v18h18" />
              <path d="m19 9-5 5-4-4-3 3" />
            </svg>
            {scopeLabel} · Academic Year 2025–26
          </div>
          <h1>
            Maharashtra Education Dashboard <span className="sov-text-gradient">(Pune Data)</span>
          </h1>
          <p>
            Single district command centre for school health, enrolment, identity compliance, infrastructure
            readiness and block-level action planning.
          </p>
        </div>
        <div className="sov-header-actions">
          <button type="button" className="sov-icon-btn" title="Export">
            <svg className="sov-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <path d="M7 10l5 5 5-5" />
              <path d="M12 15V3" />
            </svg>
          </button>
          <button type="button" className="sov-btn-primary" onClick={fetchData} data-testid="refresh-btn">
            <svg className="sov-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 12a9 9 0 1 1-2.64-6.36" />
              <path d="M21 3v6h-6" />
            </svg>
            Refresh
          </button>
        </div>
      </section>

      <section className="sov-hero-card" aria-label="State overview command centre">
        <div className="sov-hero-layout">
          <div>
            <div className="sov-score-main">
              <div
                className="sov-gauge"
                style={gaugeStyle}
                aria-label={`Average Overall School Health ${scopeKpi?.avg_shi ?? 0}`}
              >
                <div>
                  <strong>{scopeKpi?.avg_shi ?? "—"}</strong>
                  <span>Avg OSH</span>
                </div>
              </div>
              <div className="sov-score-copy">
                <h2 className="dashboard-kpi-hero-title">
                  {(scopeKpi?.avg_shi ?? 0) >= 90
                    ? "District health is excellent, but attention blocks need targeted follow-up."
                    : "District health needs coordinated follow-up across priority blocks."}
                </h2>
                <p>
                  {scopedBlocks.length} block{scopedBlocks.length === 1 ? "" : "s"} in current filter scope. The dashboard separates executive
                  status, domain risks and block-level actions so officers can move from monitoring to intervention
                  quickly.
                </p>
                <div className="sov-status-row">
                  <HeroStatusChip chipClass="sov-status-chip">
                    {excellentBlocks || scopedBlocks.length} Excellent Blocks
                  </HeroStatusChip>
                  <HeroStatusChip chipClass="sov-status-chip" variant="blue">
                    {fmt(scopeKpi?.total_schools)} Schools
                  </HeroStatusChip>
                  {attentionBlocks > 0 ? (
                    <HeroStatusChip
                      chipClass="sov-status-chip"
                      variant="orange"
                      onClick={() => scrollTo(blocksRef)}
                      ariaLabel={`${attentionBlocks} attention blocks — view block performance`}
                    >
                      {attentionBlocks} Attention Blocks
                    </HeroStatusChip>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="sov-summary-note">
              <div className="sov-icon-box green">
                <svg className="sov-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
                  <path d="m9 12 2 2 4-4" />
                </svg>
              </div>
              <div>
                <b>Primary action</b>
                <span>{primaryActionText}</span>
              </div>
            </div>
          </div>

          <div className="sov-metric-grid" aria-label="Key district KPIs">
            <article className="sov-metric-card">
              <small>Total Schools</small>
              <strong>{fmt(scopeKpi?.total_schools)}</strong>
              <span>{metricSubtitles.schools}</span>
            </article>
            <article className="sov-metric-card">
              <small>Total Students</small>
              <strong>{fmt(scopeKpi?.total_students)}</strong>
              <span>{metricSubtitles.students}</span>
            </article>
            <article className="sov-metric-card">
              <small>Aadhaar Compliance</small>
              <strong>{scopeKpi?.aadhaar_percentage ?? "—"}%</strong>
              <span>{metricSubtitles.aadhaar}</span>
            </article>
            <article className="sov-metric-card">
              <small>APAAR Coverage</small>
              <strong>{scopeKpi?.apaar_percentage ?? "—"}%</strong>
              <span className={metricSubtitles.apaarWarn ? "red" : ""}>{metricSubtitles.apaar}</span>
            </article>
          </div>
        </div>
      </section>

      <div className="sov-section-title">
        <div>
          <h2>Domain Performance Snapshot</h2>
          <p>
            Grouped domain cards replace scattered KPI tiles and help users understand where the district is strong,
            stable or needs review.
          </p>
        </div>
        <button type="button" className="sov-section-action" onClick={() => scrollTo(blocksRef)}>
          View all blocks →
        </button>
      </div>

      <section className="sov-domain-grid">
        <article className="sov-domain-card">
          <div className="sov-domain-head">
            <div className="sov-icon-box">
              <svg className="sov-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
                <path d="m9 12 2 2 4-4" />
              </svg>
            </div>
            <span className="sov-status-dot" />
          </div>
          <h3>Student Identity</h3>
          <div className="sov-domain-score">
            <strong>{identityIndex}</strong>
            <span>Index</span>
          </div>
          <div className="sov-progress-line">
            <i style={{ width: `${identityIndex}%` }} />
          </div>
          <div className="sov-domain-meta">
            <span>Aadhaar {scopeKpi?.aadhaar_percentage ?? "—"}%</span>
            <span>APAAR {scopeKpi?.apaar_percentage ?? "—"}%</span>
          </div>
        </article>

        <article className="sov-domain-card">
          <div className="sov-domain-head">
            <div className="sov-icon-box green">
              <svg className="sov-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 21h18" />
                <path d="M5 21V7l8-4v18" />
                <path d="M19 21V11l-6-4" />
              </svg>
            </div>
            <span className="sov-status-dot" />
          </div>
          <h3>Infrastructure</h3>
          <div className="sov-domain-score">
            <strong>{classroomHealth}%</strong>
            <span>Classroom</span>
          </div>
          <div className="sov-progress-line">
            <i style={{ width: `${classroomHealth}%`, background: "var(--sov-accent)" }} />
          </div>
          <div className="sov-domain-meta">
            <span>Water {district?.water_percentage ?? scopeKpi?.water_availability_percentage ?? "—"}%</span>
            <span>Toilet {district?.toilet_percentage ?? "—"}%</span>
          </div>
        </article>

        <article className="sov-domain-card">
          <div className="sov-domain-head">
            <div className="sov-icon-box purple">
              <svg className="sov-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="8" r="4" />
                <path d="M6 21v-2a6 6 0 0 1 12 0v2" />
              </svg>
            </div>
            <span className="sov-status-dot amber" />
          </div>
          <h3>Staffing Load</h3>
          <div className="sov-domain-score">
            <strong>{Math.round(scopeKpi?.avg_ptr ?? 0)}</strong>
            <span>PTR</span>
          </div>
          <div className="sov-progress-line">
            <i style={{ width: "72%", background: "var(--sov-purple)" }} />
          </div>
          <div className="sov-domain-meta">
            <span>{(scopeKpi?.avg_ptr ?? 0) > 30 ? "Review needed" : "Stable"}</span>
            <span>Pune avg</span>
          </div>
        </article>

        <article className="sov-domain-card">
          <div className="sov-domain-head">
            <div className="sov-icon-box orange">
              <svg className="sov-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 4h14v16H5z" />
                <path d="M9 2h6v4H9z" />
                <path d="M9 11l2 2 4-4" />
              </svg>
            </div>
            <span className="sov-status-dot" />
          </div>
          <h3>Operations</h3>
          <div className="sov-domain-score">
            <strong>{scopeKpi?.data_entry_percentage ?? "—"}%</strong>
            <span>Data Entry</span>
          </div>
          <div className="sov-progress-line">
            <i style={{ width: `${scopeKpi?.data_entry_percentage ?? 0}%`, background: "var(--sov-secondary)" }} />
          </div>
          <div className="sov-domain-meta">
            <span>{(scopeKpi?.data_entry_percentage ?? 0) >= 95 ? "Complete" : "In progress"}</span>
            <span>Stable</span>
          </div>
        </article>
      </section>

      <div className="sov-section-title">
        <div>
          <h2>Block Health Distribution</h2>
          <p>
            Separate distribution, top performers and attention blocks so leaders can scan the district without reading
            every card.
          </p>
        </div>
        <button type="button" className="sov-section-action" onClick={() => scrollTo(insightsRef)}>
          View recommendations →
        </button>
      </div>

      <section className="sov-overview-grid">
        <article className="sov-panel">
          <div className="sov-panel-head">
            <div>
              <h3>Health Distribution</h3>
              <p>
                {ragDistribution.critical === 0 && ragDistribution.atRisk === 0
                  ? "All monitored blocks are currently classified as excellent."
                  : "Block health distribution across monitored Pune blocks."}
              </p>
            </div>
            <span className="sov-pill green">{blocks.length} blocks</span>
          </div>
          <div className="sov-donut-panel">
            <div>
              <div className="sov-donut" style={donutStyle} />
              <div className="sov-legend">
                <span>
                  <i className="sov-dot" /> Excellent: {ragDistribution.excellent}
                </span>
                <span>
                  <i className="sov-dot orange" /> At Risk: {ragDistribution.atRisk}
                </span>
                <span>
                  <i className="sov-dot red" /> Critical: {ragDistribution.critical}
                </span>
              </div>
            </div>
          </div>
        </article>

        <article className="sov-panel">
          <div className="sov-panel-head">
            <div>
              <h3>Top Performing Blocks</h3>
              <p>Highest composite OSH scores in Pune district.</p>
            </div>
            <span className="sov-pill green">Best 5</span>
          </div>
          <div className="sov-rank-list">
            {topBlocks.map((block, idx) => (
              <div
                key={block.block_code}
                className="sov-rank-item"
                onClick={() => navigate(`/block/${block.block_code}`)}
                data-testid={`top-block-${idx}`}
              >
                <span className="sov-rank-num">{idx + 1}</span>
                <span className="sov-rank-title">
                  <b>{block.block_name}</b>
                  <span>{block.total_schools} schools</span>
                </span>
                <span className="sov-rank-score">
                  <strong>{block.shi_score}</strong>
                  <span>OSH Score</span>
                </span>
              </div>
            ))}
          </div>
        </article>

        <article className="sov-panel">
          <div className="sov-panel-head">
            <div>
              <h3>Blocks Needing Attention</h3>
              <p>Still healthy, but lower scores than the district peer group.</p>
            </div>
            <span className="sov-pill red">Monitor</span>
          </div>
          <div className="sov-rank-list">
            {bottomBlocks.map((block, idx) => (
              <div
                key={block.block_code}
                className="sov-rank-item"
                onClick={() => navigate(`/block/${block.block_code}`)}
                data-testid={`bottom-block-${idx}`}
              >
                <span className="sov-rank-num red">{idx + 1}</span>
                <span className="sov-rank-title">
                  <b>{block.block_name}</b>
                  <span>{block.total_schools} schools</span>
                </span>
                <span className="sov-rank-score">
                  <strong>{block.shi_score}</strong>
                  <span>OSH Score</span>
                </span>
              </div>
            ))}
          </div>
        </article>
      </section>

      <div className="sov-section-title" ref={insightsRef}>
        <div>
          <h2>District Summary and Executive Actions</h2>
          <p>State Overview should not only report data; it should guide the next review agenda and block-wise follow-up.</p>
        </div>
        <button type="button" className="sov-section-action" onClick={() => scrollTo(topRef)}>
          Back to top ↑
        </button>
      </div>

      <section className="sov-insight-grid">
        <article className="sov-district-card">
          <div className="sov-district-head">
            <div className="sov-district-name">
              <div className="sov-icon-box">
                <svg className="sov-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 1 1 16 0Z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
              </div>
              <span>
                <b>{district?.district_name || "Pune District"}</b>
                <span>Code: {district?.district_code || PUNE_DISTRICT_CODE}</span>
              </span>
            </div>
            <span className={districtBadge.className}>{districtBadge.label}</span>
          </div>
          <div className="sov-kv">
            <div className="sov-kv-row">
              <span>Schools</span>
              <b>{fmt(district?.total_schools ?? scopeKpi?.total_schools)}</b>
            </div>
            <div className="sov-kv-row">
              <span>Students</span>
              <b>{fmt(district?.total_students ?? scopeKpi?.total_students)}</b>
            </div>
            <div className="sov-kv-row">
              <span>OSH Score</span>
              <b>{district?.shi_score ?? scopeKpi?.avg_shi ?? "—"}</b>
            </div>
            <div className="sov-mini-progress">
              <i style={{ width: `${district?.shi_score ?? scopeKpi?.avg_shi ?? 0}%` }} />
            </div>
            <button
              type="button"
              className="sov-view-link"
              onClick={() => navigate(`/district/${PUNE_DISTRICT_CODE}`)}
            >
              View district drilldown ›
            </button>
          </div>
        </article>

        <div className="sov-action-cards">
          <article className="sov-action-item">
            <div className="sov-icon-box green">
              <svg className="sov-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 3v18h18" />
                <path d="m19 9-5 5-4-4-3 3" />
              </svg>
            </div>
            <h4>Protect top-block performance</h4>
            <p>
              Use {topBlocks[0]?.block_name || "top blocks"}
              {topBlocks[1] ? ` and ${topBlocks[1].block_name}` : ""} as benchmark blocks for school health practices
              and reporting discipline.
            </p>
            <span className="sov-action-meta">
              {topBlocks[0]?.shi_score ? `OSH ${topBlocks[0].shi_score}% benchmark` : "Top block benchmark"}
            </span>
          </article>
          <article className="sov-action-item">
            <div className="sov-icon-box orange">
              <svg className="sov-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
              </svg>
            </div>
            <h4>Close APAAR gap</h4>
            <p>
              Aadhaar is strong at {scopeKpi?.aadhaar_percentage ?? "—"}%, but APAAR at{" "}
              {scopeKpi?.apaar_percentage ?? "—"}% should be the next compliance focus.
            </p>
            <span className="sov-action-meta">
              Gap {Math.max(0, Math.round((scopeKpi?.aadhaar_percentage ?? 0) - (scopeKpi?.apaar_percentage ?? 0)))}%
            </span>
          </article>
          <article className="sov-action-item">
            <div className="sov-icon-box red">
              <svg className="sov-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 8v4" />
                <path d="M12 16h.01" />
              </svg>
            </div>
            <h4>Monitor lower OSH cluster</h4>
            <p>
              {bottomBlocks
                .slice(0, 3)
                .map((b) => b.block_name)
                .join(", ") || "Priority blocks"}{" "}
              are not critical, but they require targeted review before they fall behind.
            </p>
            <span className="sov-action-meta">
              {bottomBlocks[0]?.shi_score ? `Lowest OSH ${bottomBlocks[0].shi_score}%` : "30-day review"}
            </span>
          </article>
        </div>
      </section>

      <div className="sov-block-toolbar" ref={blocksRef}>
        <div className="sov-toolbar-title">
          <b>All Blocks in Pune</b>
          <span>Card layout keeps block comparison scannable without crowding the page.</span>
        </div>
        <div className="sov-toolbar-actions">
          <select
            className="sov-select-chip"
            aria-label="Sort blocks"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            data-testid="sort-select"
          >
            <option value="shi_score">Sort by OSH Score</option>
            <option value="total_schools">Sort by schools</option>
            <option value="total_students">Sort by students</option>
            <option value="block_name">Sort by name</option>
          </select>
          <div className="sov-toggle">
            <button
              type="button"
              className={view === "grid" ? "active" : ""}
              onClick={() => setView("grid")}
              data-testid="view-grid"
            >
              Grid
            </button>
            <button
              type="button"
              className={view === "table" ? "active" : ""}
              onClick={() => setView("table")}
              data-testid="view-table"
            >
              Table
            </button>
          </div>
        </div>
      </div>

      {view === "grid" ? (
        <section className="sov-blocks-grid" aria-label="All blocks in Pune">
          {sortedBlocks.map((block) => {
            const badge = getBlockBadge(block.shi_score);
            const progressWarn = block.shi_score < 90;
            return (
              <article
                key={block.block_code}
                className="sov-block-card"
                onClick={() => navigate(`/block/${block.block_code}`)}
                data-testid={`district-card-${block.block_code}`}
              >
                <div className="sov-block-head">
                  <div className="sov-block-name">
                    <div className="sov-icon-box">
                      <svg className="sov-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 1 1 16 0Z" />
                        <circle cx="12" cy="10" r="3" />
                      </svg>
                    </div>
                    <span>
                      <b>{block.block_name}</b>
                      <span>Code: {block.block_code}</span>
                    </span>
                  </div>
                  <span className={badge.className}>{badge.label}</span>
                </div>
                <div className="sov-block-stats">
                  <div className="sov-block-row">
                    <span>Schools</span>
                    <b>{fmt(block.total_schools)}</b>
                  </div>
                  <div className="sov-block-row">
                    <span>Students</span>
                    <b>{fmt(block.total_students)}</b>
                  </div>
                  <div className="sov-block-row">
                    <span>OSH Score</span>
                    <b>{block.shi_score}</b>
                  </div>
                  <div className="sov-mini-progress">
                    <i
                      style={{
                        width: `${block.shi_score}%`,
                        background: progressWarn ? "var(--sov-warning)" : "var(--sov-success)",
                      }}
                    />
                  </div>
                </div>
                <div className="sov-view-link">View Details ›</div>
              </article>
            );
          })}
        </section>
      ) : (
        <div className="sov-table-wrap">
          <DataTable
            data={sortedBlocks}
            columns={blockTableColumns}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={(key) => setSortBy(key)}
            onRowClick={(row) => navigate(`/block/${row.block_code}`)}
            testId="blocks-table"
          />
        </div>
      )}
    </div>
  );
};

export default StateOverview;
